import { useState } from "react"
import { ActivityIndicator, StyleSheet, Text, TextStyle, View, ViewStyle } from "react-native"

import Ionicons from "@expo/vector-icons/Ionicons"
import { Image } from "expo-image"

import { useAppTheme } from "src/hooks/useAppTheme"
import { useWebsiteThumbnail } from "src/hooks/useWebsiteThumbnail"
import { ThemedStyle } from "src/theme"

interface WebsiteThumbnailProps {
  websiteUrl: string
  /** Pre-provided thumbnail URL from the server. When set, skips OG/scrape fetching. */
  thumbnailUrl?: string
}

const FALLBACK_ICON_SIZE = 72

export const WebsiteThumbnail = ({ websiteUrl, thumbnailUrl }: WebsiteThumbnailProps) => {
  // Only run the scrape-based fetch when the server hasn't provided a thumbnail URL.
  const {
    title,
    imageUrl: scrapedImageUrl,
    loading,
  } = useWebsiteThumbnail(thumbnailUrl ? null : websiteUrl)
  // Track image-load failures so we can swap in the vector globe fallback.
  const [imageFailed, setImageFailed] = useState(false)
  const {
    themed,
    theme: { colors },
  } = useAppTheme()

  // Prefer the server-provided thumbnail; fall back to OG/scraped image.
  const resolvedImageUrl = thumbnailUrl ?? scrapedImageUrl

  // Show the OG/scraped image when available, otherwise a crisp vector globe.
  // A vector icon scales sharply at any size, unlike a bundled PNG whose density
  // variants (@2x/@3x) are picked by device pixel ratio and can't satisfy a large render.
  const showFallbackIcon = imageFailed || !resolvedImageUrl

  return (
    <View>
      <View style={themed($thumbnailContainer)}>
        <>
          <View style={$imageContainer}>
            {loading ? (
              <ActivityIndicator />
            ) : showFallbackIcon ? (
              <Ionicons name="globe-outline" size={FALLBACK_ICON_SIZE} color={colors.textDim} />
            ) : (
              <Image
                source={{ uri: resolvedImageUrl! }}
                contentFit="contain"
                style={{ height: 100, width: "100%" }}
                onError={(e) => {
                  console.warn(
                    `Using fallback thumbnail because image ${resolvedImageUrl} failed to load: ${e.error}`,
                  )
                  setImageFailed(true)
                }}
              />
            )}
          </View>
          <Text style={themed($text)} numberOfLines={1}>
            {loading ? "Loading..." : title ? `${title} - ${websiteUrl}` : websiteUrl}
          </Text>
        </>
      </View>
    </View>
  )
}

const $thumbnailContainer: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  backgroundColor: colors.background,
  padding: 0,
  borderRadius: 10,
  borderColor: colors.border,
  borderWidth: StyleSheet.hairlineWidth * 3,
  marginBottom: spacing.xs,
  overflow: "hidden",
})

const $imageContainer: ViewStyle = {
  height: 100,
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
}

const $text: ThemedStyle<TextStyle> = ({ colors, spacing, typography }) => ({
  color: colors.text,
  padding: spacing.xs,
  backgroundColor: colors.tintInactive,
  borderBottomLeftRadius: 10,
  borderBottomRightRadius: 10,
  fontFamily: typography.fonts.figtree.normal,
})
